import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, Text, TouchableOpacity, View, Image, Pressable } from 'react-native';
import { quarterSharedStyles } from '../quarter/quarterSharedStyles';
import AntDesign from 'react-native-vector-icons/AntDesign';
import { Spacer } from '../../../../helperComponents/Spacer';
import { MB_Button } from '@mightybyte/rnw.components.button';
import { mbTextStyles } from '@mightybyte/rnw.utils.style-utils';
import { textStyles } from '../../../../../constants/textStyles';
import { getColors } from '../../../../../constants/colors';
import { MB_TextInput, MB_TextInputInputType } from '@mightybyte/rnw.components.text-input';
import DatePicker from '../../../../helperComponents/DatePicker';
import { sub, endOfToday } from 'date-fns';
import { MiniGameForm } from './MiniGameView';
import { MB_ResourceUploader, MB_RESOURCE_PICKER_TYPE, MB_ResourceUploaderFunctionProps } from '@mightybyte/rnw.components.resource-uploader';
import { MB_utilHooks } from '@mightybyte/rnw.utils.util-hooks';
import { resourceUploadApiCalls } from '../../../../../apiCalls/resourceUploadApiCalls';
import { ImageFileExtension } from '@sapphicsavvy/business';
import { utils } from '../../../../../utils/utils';
import NoImageIcon from '../../../../../resources/svgComponents/NoImageIcon';
import TrashIcon from '../../../../../resources/svgComponents/TrashIcon';
import { trpc } from '../../../../../apiCalls/trpcClient';
import { MB_Modal } from '@mightybyte/rnw.components.modal';
import { AdminConfirmPopup } from '../quarter/ConfirmActionUI';

const COLORS = getColors();

type Props = {
  onBackButtonPress: () => void;
  onSaveGame: (game: MiniGameForm) => void;
  onAddNewGame: () => void;
  onDeleteGame: (index: number) => void;
  onSaveSetInfo: () => void;
  setTitle: (value: string) => void;
  setLink: (value: string) => void;
  setImageUrl: (value: string | undefined) => void;
  setNumber?: number;
  setSetNumber: (value: number) => void;
  commonStartDate?: Date;
  setCommonStartDate: (date: Date | undefined) => void;
  commonEndDate?: Date;
  setCommonEndDate: (date: Date | undefined) => void;
  isNewSet: boolean;
  miniGames: MiniGameForm[];
  setMiniGames: (games: MiniGameForm[]) => void;
  currentGameIndex: number;
  setCurrentGameIndex: (index: number) => void;
}

const MiniGameViewUI = ({
  onBackButtonPress,
  onSaveGame,
  onAddNewGame,
  onDeleteGame,
  onSaveSetInfo,
  setTitle,
  setLink,
  setImageUrl,
  setNumber,
  setSetNumber,
  commonStartDate,
  setCommonStartDate,
  commonEndDate,
  setCommonEndDate,
  isNewSet,
  miniGames,
  setMiniGames,
  currentGameIndex,
  setCurrentGameIndex,
}: Props) => {

  const [isResourcePickerVisible, showResourcePicker, hideResourcePicker] = MB_utilHooks.useBool();
  const [tempUrl, setTempUrl] = useState<string | undefined>(undefined);
  const [gameToDelete, setGameToDelete] = useState<number | undefined>(undefined);

  const { mutate: getUploadUrl } = trpc.admin.quarters.getQuestionImageUploadUrl.useMutation();

  // Image uploader function
  const imageUploader = (uploadProgressListener: (percentCompleted: number) => void) => async (fileProps: MB_ResourceUploaderFunctionProps): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!fileProps.blob) {
        reject('Something went wrong');
        return;
      }

      const extension = utils.enumFromStringValue(ImageFileExtension, fileProps.mimeType.split('/')[1]);

      if (!extension) {
        reject('Error: Invalid image format');
        return;
      }

      getUploadUrl({ fileExtension: extension }, {
        onSuccess: async (data) => {
          resourceUploadApiCalls.uploadResource({
            method: data.requestMethod,
            url: data.signedUrl,
            imageData: fileProps.blob!,
            headers: data.headers,
            uploadProgress: uploadProgressListener,
          })
            .then(() => {
              const updatedGames = [...miniGames];
              updatedGames[currentGameIndex] = { ...updatedGames[currentGameIndex], imageUrl: data.fileName };
              setMiniGames(updatedGames);
              setImageUrl(data.fileName);
              setTempUrl(fileProps.uri);
              resolve();
            })
            .catch(() => reject('Something went wrong during upload'));
        },
        onError: (error) => {
          console.log('Error data', error);
          reject('Failed to get upload URL');
        },
      });
    });
  };

  const hasUnsavedGame = useMemo(() => {
    return miniGames.some(game => !game.id);
  }, [miniGames]);

  const onDeleteConfirm = useCallback(() => {
    if (gameToDelete !== undefined) {
      onDeleteGame(gameToDelete);
    }
    setGameToDelete(undefined);
  }, [gameToDelete, onDeleteGame]);

  const gameDeleteNote = useMemo(() => {
    if (gameToDelete !== undefined) {
      const game = miniGames[gameToDelete];
      return game?.title || `Game ${gameToDelete + 1}`;
    }
    return undefined;
  }, [gameToDelete, miniGames]);

  const renderGameCard = useCallback((game: MiniGameForm, index: number) => {
    return (
      <View style={styles.gameCard} key={index}>
        <View style={styles.gameCardHeader}>
          <Text style={styles.gameCardTitle}>Game {index + 1}</Text>
          {miniGames.length > 1 && (
            <TouchableOpacity
              onPress={() => setGameToDelete(index)}
              style={styles.deleteCardButton}
            >
              <TrashIcon size={16} />
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.gameCardContent}>
          {/* Left Column: Inputs */}
          <View style={styles.leftColumn}>
            <View style={styles.questionBlock}>
              <View>
                <MB_TextInput
                    title={'Title'}
                    onChangeText={(text) => {
                      const updatedGame = { ...game, title: text };
                      const updatedGames = [...miniGames];
                      updatedGames[index] = updatedGame;
                      setMiniGames(updatedGames);
                      if (index === currentGameIndex) {
                        setTitle(text);
                      }
                    }}
                    value={game.title}
                    showRequiredAsteriks
                    titleStyle={quarterSharedStyles.textInputTitle}
                    textInputStyle={quarterSharedStyles.textInputStyle}
                    style={styles.inputStyle}
                />

                <Spacer height={16} />

                <MB_TextInput
                    title={'Link'}
                    onChangeText={(text) => {
                      const updatedGame = { ...game, link: text };
                      const updatedGames = [...miniGames];
                      updatedGames[index] = updatedGame;
                      setMiniGames(updatedGames);
                      if (index === currentGameIndex) {
                        setLink(text);
                      }
                    }}
                    value={game.link}
                    showRequiredAsteriks
                    titleStyle={quarterSharedStyles.textInputTitle}
                    textInputStyle={quarterSharedStyles.textInputStyle}
                    style={styles.inputStyle}
                />

                <Spacer height={16} />
              </View>


              <View style={styles.saveButtonWrapper}>
                <MB_Button
                  title="Save Game"
                  onPress={() => {
                    setCurrentGameIndex(index);
                    setTitle(game.title);
                    setLink(game.link);
                    setImageUrl(game.imageUrl);
                    onSaveGame(game);
                  }}
                  style={quarterSharedStyles.button}
                  textStyle={quarterSharedStyles.buttonText}
                />
              </View>
            </View>
          </View>

          {/* Right Column: Image Upload */}
          <View style={styles.rightColumn}>
            <View style={[styles.questionBlock, styles.imageContainer]}>
              <Text style={quarterSharedStyles.fakeTitle}>Upload mini game image</Text>
              <Text style={styles.imageNote}>
                Required: 4:3 ratio (e.g., 400×300, 800×600, 1200×900)
              </Text>
              <View style={styles.fileUploadWrapper}>
                <View style={styles.chooseFileSubsection}>
                  <Pressable
                    style={styles.chooseFileLeft}
                    onPress={() => {
                      setCurrentGameIndex(index);
                      setTitle(game.title);
                      setLink(game.link);
                      setImageUrl(game.imageUrl);
                      showResourcePicker();
                    }}
                  >
                    <Text style={{ color: COLORS.white }}>Choose file</Text>
                  </Pressable>
                  <TouchableOpacity
                    onPress={() => {
                      const updatedGame = { ...game, imageUrl: undefined };
                      const updatedGames = [...miniGames];
                      updatedGames[index] = updatedGame;
                      setMiniGames(updatedGames);
                      if (index === currentGameIndex) {
                        setImageUrl(undefined);
                        setTempUrl(undefined);
                      }
                    }}
                    style={styles.iconButton}
                  >
                    <TrashIcon size={16} />
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.imagePreviewContainer}>
                {(index === currentGameIndex && tempUrl) ? (
                  <Image style={styles.imagePreview} source={{ uri: tempUrl }} />
                ) : game.imageUrl ? (
                  <Image style={styles.imagePreview} source={{ uri: game.imageUrl }} />
                ) : (
                  <NoImageIcon />
                )}
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  }, [miniGames, currentGameIndex, setTitle, setLink, setImageUrl, showResourcePicker, setMiniGames, setCurrentGameIndex, tempUrl, onSaveGame]);

  return (
    <>
      <MB_Modal
        isVisible={gameToDelete !== undefined}
        hideCloseButton={true}
        childrenWrapperStyle={{ padding: 0 }}
      >
        <AdminConfirmPopup
          title={'Delete Mini Game'}
          middle={gameDeleteNote}
          note={'Are you sure you want to delete this mini game? This action cannot be undone.'}
          buttons={[
            <MB_Button
              title={'Delete'}
              onPress={onDeleteConfirm}
              style={[quarterSharedStyles.secondaryBtn, { width: 150, marginRight: 20 }]}
              textStyle={quarterSharedStyles.secondaryBtnText}
            />,
            <MB_Button
              title={'Cancel'}
              onPress={() => setGameToDelete(undefined)}
              style={[quarterSharedStyles.button, { width: 150 }]}
              textStyle={quarterSharedStyles.buttonText}
            />,
          ]}
        />
      </MB_Modal>
      <MB_ResourceUploader
        isVisible={isResourcePickerVisible}
        onDismiss={hideResourcePicker}
        uploaderFunction={imageUploader}
        pageTitle={'Select mini game image'}
        uploadButtonTitle="Upload"
        resourcePickerType={MB_RESOURCE_PICKER_TYPE.image}
        uploadButtonStyle={quarterSharedStyles.button}
        uploadButtonTextStyle={quarterSharedStyles.buttonText}
        dragAndDropAndProcessingResourceButtonColor={COLORS.accentColor}
        dragAndDropAndProcessingResourceTextStyle={quarterSharedStyles.secondaryBtnText}
        previewContainerStyle={styles.uploadModalPreview}
      />
      <View style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity onPress={onBackButtonPress} style={styles.backButton}>
            <AntDesign name="arrowleft" size={24} color={COLORS.black} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>
            {isNewSet ? 'Add New Mini Games' : `Edit Mini Games - Set ${setNumber}`}
          </Text>
        </View>

        <View style={styles.content}>
          {/* Common Set Info */}
          <View style={styles.section}>
            <View style={styles.setInfoHeader}>
              <Text style={quarterSharedStyles.titleText}>Set Information</Text>
              {!isNewSet && (
                <MB_Button
                  title="Save Set Info"
                  onPress={onSaveSetInfo}
                  style={[quarterSharedStyles.button, { height: 40 }]}
                  textStyle={[quarterSharedStyles.buttonText, { fontSize: 14 }]}
                />
              )}
            </View>
            <Spacer height={16} />
            <View style={styles.row}>
              <MB_TextInput
                title={'Set Number'}
                onChangeText={(text) => setSetNumber(Number(text))}
                value={setNumber?.toString()}
                showRequiredAsteriks
                inputType={MB_TextInputInputType.numbers}
                titleStyle={quarterSharedStyles.textInputTitle}
                textInputStyle={quarterSharedStyles.textInputStyle}
                style={quarterSharedStyles.smallInput}
              />
              <Spacer width={40} />
              <View>
                <DatePicker
                  title={'Start Date'}
                  minDate={sub(endOfToday(), { days: 0 })}
                  onDateChange={setCommonStartDate}
                  showRequiredAsteriks
                  date={commonStartDate}
                  titleStyle={quarterSharedStyles.textInputTitle}
                />
              </View>
              <Spacer width={40} />
              <View>
                <DatePicker
                  title={'End Date'}
                  minDate={commonStartDate ?? sub(endOfToday(), { days: 0 })}
                  onDateChange={setCommonEndDate}
                  showRequiredAsteriks
                  date={commonEndDate}
                  titleStyle={quarterSharedStyles.textInputTitle}
                />
              </View>
            </View>
            <Text style={styles.helpText}>
              {isNewSet
                ? 'Set number and dates will be applied when you save your first game'
                : 'Click "Save Set Info" to update set number and dates for all games in this set'}
            </Text>
          </View>

          <Spacer height={30} />

          {/* Game Cards */}
          <View style={styles.gamesHeader}>
            <Text style={quarterSharedStyles.titleText}>Mini Games</Text>
            <MB_Button
              leftElement={<AntDesign name="plus" size={16} color="white" />}
              title="Add Game"
              onPress={onAddNewGame}
              style={[quarterSharedStyles.button, { height: 40 }]}
              textStyle={[quarterSharedStyles.buttonText, { fontSize: 14 }]}
              disabled={hasUnsavedGame}
            />
          </View>

          <Spacer height={20} />

          {miniGames.map((game, index) => renderGameCard(game, index))}
        </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 20,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.strokes,
  },
  backButton: {
    padding: 10,
  },
  headerTitle: mbTextStyles([textStyles.largeText, {
    fontSize: 22,
    color: COLORS.black,
    marginLeft: 10,
    fontWeight: 'bold',
  }]),
  content: {
    padding: 20,
  },
  section: {
    backgroundColor: COLORS.backgroundPrimary,
    borderRadius: 12,
    padding: 20,
  },
  setInfoHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  helpText: mbTextStyles([textStyles.smallText, {
    color: COLORS.greyText,
    marginTop: 10,
    fontStyle: 'italic',
    textAlign: 'left',
  }]),
  gamesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  gameCard: {
    backgroundColor: COLORS.backgroundPrimary,
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
  },
  gameCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  gameCardTitle: mbTextStyles([textStyles.largerText, {
    color: COLORS.textPrimary,
    fontWeight: 'bold',
    fontSize: 22,

  }]),
  gameCardContent: {
    flexDirection: 'row',
  },
  leftColumn: {
    flex: 1,
    marginRight: 12,
    alignSelf: 'stretch',
  },
  rightColumn: {
    width: 400,
  },
  deleteCardButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.strokes,
    borderRadius: 8,
  },
  questionBlock: {
    backgroundColor: COLORS.lighterGray,
    padding: 16,
    borderRadius: 5,
    flex: 1,
    justifyContent: 'space-between',
  },
  imageContainer: {
    width: 400,
  },
  imageNote: mbTextStyles([textStyles.smallText, {
    color: COLORS.greyText,
    fontSize: 12,
    marginTop: 4,
    marginBottom: 12,
    textAlign: 'left',
  }]),
  inputStyle: {
    backgroundColor: COLORS.white,
    width: '100%',
  },
  saveButtonWrapper: {
    alignItems: 'flex-start',
  },
  imagePreviewContainer: {
    borderRadius: 5,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: COLORS.strokes,
    width: '100%',
    aspectRatio: 4 / 3,
    backgroundColor: '#F9F9F9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  fileUploadWrapper: {
    width: '100%',
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  chooseFileSubsection: {
    padding: 0,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 200,
    width: '100%',
    justifyContent: 'space-between',
  },
  chooseFileLeft: {
    borderRadius: 8,
    backgroundColor: COLORS.orange,
    width: 150,
    height: 41,
    paddingLeft: 16,
    justifyContent: 'center',
    marginRight: 10,
  },
  iconButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.strokes,
    borderRadius: 8,
  },
  uploadModalPreview: {
    aspectRatio: 4 / 3,
  },
});

export { MiniGameViewUI };
